import { useEffect, useMemo, useState } from 'react';
import {
  Briefcase, Search, Mail, Phone, Smartphone, MapPin, RefreshCw,
  AlertCircle, UserPlus, TrendingUp, MessageCircle, X, Check,
} from 'lucide-react';
import { apiUrl } from '../../../utils/apiBase';
import { crmApi, Funil } from '../crmApi';

interface Props { session: any; }

interface ClienteGix {
  codigo: string;
  nome: string;
  inativo: string;
  cidade: string;
  estado: string;
  cnpj: string;
  email: string;
  telefone: string;
  celular: string;
  contato_nome: string;
}

// ---- Toast simples ----
interface Toast { id: number; tipo: 'ok' | 'err'; msg: string; }

// ---- Modal Adicionar ao Funil ----
interface ModalFunilProps {
  cliente: ClienteGix;
  session: any;
  onClose: () => void;
  onSucesso: (msg: string) => void;
}

const ModalAdicionarFunil = ({ cliente, session, onClose, onSucesso }: ModalFunilProps) => {
  const [funis, setFunis] = useState<Funil[]>([]);
  const [funilId, setFunilId] = useState('');
  const [etapaId, setEtapaId] = useState('');
  const [titulo, setTitulo] = useState(cliente.nome.slice(0, 80));
  const [valor, setValor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    crmApi.listarFunis(session).then(lista => {
      setFunis(lista);
      if (lista.length > 0) {
        setFunilId(lista[0].id);
        if (lista[0].etapas?.length > 0) setEtapaId(lista[0].etapas[0].id);
      }
    });
  }, []);// eslint-disable-line

  const funilAtual = funis.find(f => f.id === funilId);

  const handleFunilChange = (id: string) => {
    setFunilId(id);
    const f = funis.find(x => x.id === id);
    setEtapaId(f?.etapas?.[0]?.id || '');
  };

  const salvar = async () => {
    if (!funilId || !etapaId) { setErro('Selecione o funil e a etapa.'); return; }
    setSalvando(true); setErro('');
    try {
      await crmApi.criarNegocio(session, {
        titulo: titulo || cliente.nome,
        funilId,
        etapaId,
        cliente: cliente.nome,
        email: cliente.email || undefined,
        telefone: cliente.celular || cliente.telefone || undefined,
        valor: valor ? parseFloat(valor.replace(',', '.')) : undefined,
        responsavelId: session?.usuario?.email || undefined,
        camposCustom: { gix_codigo: cliente.codigo, gix_cnpj: cliente.cnpj },
      });
      onSucesso(`"${titulo || cliente.nome}" adicionado ao funil!`);
      onClose();
    } catch (e: any) {
      setErro(e.message || 'Erro ao criar negócio');
    } finally { setSalvando(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-blue-600" /> Adicionar ao Funil
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
        </div>

        <div className="text-xs text-gray-500 mb-4 bg-gray-50 rounded-lg px-3 py-2">
          Cliente: <strong className="text-gray-800">{cliente.nome}</strong>
          {cliente.codigo && <span className="ml-1 text-gray-400">(cod. {cliente.codigo})</span>}
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-700">Título do negócio</label>
            <input value={titulo} onChange={e => setTitulo(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-700">Funil</label>
              <select value={funilId} onChange={e => handleFunilChange(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                {funis.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Etapa</label>
              <select value={etapaId} onChange={e => setEtapaId(e.target.value)}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                {(funilAtual?.etapas || []).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700">Valor estimado (R$) <span className="text-gray-400 font-normal">— opcional</span></label>
            <input value={valor} onChange={e => setValor(e.target.value)} placeholder="0,00"
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>

        {erro && <p className="text-xs text-red-600 mt-3">{erro}</p>}

        <div className="flex gap-2 mt-4 justify-end">
          <button onClick={onClose}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancelar</button>
          <button onClick={salvar} disabled={salvando}
            className="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50">
            {salvando ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

interface Estado {
  carregando: boolean;
  erro: string;
  vendedorCodigo: string;
  vendedorNome: string;
  total: number;
  clientes: ClienteGix[];
}

const CRMCarteiraGix = ({ session }: Props) => {
  const meuEmail = (session?.usuario?.email || '').toLowerCase();
  const meuNome = session?.usuario?.nome || '';

  const [estado, setEstado] = useState<Estado>({
    carregando: true, erro: '', vendedorCodigo: '', vendedorNome: '',
    total: 0, clientes: [],
  });
  const [busca, setBusca] = useState('');
  const [incluirInativos, setIncluirInativos] = useState(false);
  const [modalFunil, setModalFunil] = useState<ClienteGix | null>(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [importando, setImportando] = useState<string | null>(null); // codigo do cliente sendo importado

  const buscarCarteira = async (codigo: string, nomeVendedor: string) => {
    const params = new URLSearchParams({ limit: '1000' });
    if (incluirInativos) params.set('incluir_inativos', '1');
    const resp = await fetch(apiUrl(`/gix/vendedores/${encodeURIComponent(codigo)}/clientes?${params}`));
    const data = await resp.json();
    if (data.status !== 'ok') throw new Error(data.message || 'Falha ao carregar carteira');
    setEstado({
      carregando: false, erro: '',
      vendedorCodigo: codigo, vendedorNome: nomeVendedor,
      total: data.total_carteira || 0,
      clientes: data.results || [],
    });
  };

  const resolverEcarregar = async () => {
    if (!meuEmail) {
      setEstado(s => ({ ...s, carregando: false, erro: 'Sessao sem email' }));
      return;
    }
    setEstado(s => ({ ...s, carregando: true, erro: '' }));
    try {
      // 1) Ja tem codigo salvo no usuario?
      const r1 = await fetch(apiUrl(`/me/vendedor-gix?userEmail=${encodeURIComponent(meuEmail)}`));
      const d1 = await r1.json();
      if (d1?.success && d1.codigo) {
        await buscarCarteira(String(d1.codigo), d1.nome || meuNome);
        return;
      }
      // 2) Auto-resolver pelo nome do logado
      if (!meuNome || meuNome.length < 3) {
        setEstado(s => ({ ...s, carregando: false, erro: 'Nome do usuario muito curto para mapear no GIX' }));
        return;
      }
      const r2 = await fetch(apiUrl(`/gix/vendedores/match?nome=${encodeURIComponent(meuNome)}&limit=5`));
      const d2 = await r2.json();
      const candidatos = (d2?.results || []) as { codigo: string; nome: string }[];
      if (!candidatos.length) {
        setEstado(s => ({
          ...s, carregando: false,
          erro: `Nao encontrei nenhum vendedor no GIX com nome parecido com "${meuNome}".`,
        }));
        return;
      }
      const escolhido = candidatos[0];
      // 3) Persiste no doc do usuario para nao precisar resolver de novo
      try {
        await fetch(apiUrl('/me/vendedor-gix'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userEmail: meuEmail, codigo: escolhido.codigo, nome: escolhido.nome }),
        });
      } catch { /* nao bloqueia se falhar o cache */ }
      await buscarCarteira(String(escolhido.codigo), escolhido.nome);
    } catch (e: any) {
      setEstado(s => ({ ...s, carregando: false, erro: e?.message || String(e) }));
    }
  };

  useEffect(() => { resolverEcarregar(); /* eslint-disable-next-line */ }, [incluirInativos]);

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return estado.clientes;
    return estado.clientes.filter(c =>
      c.nome.toLowerCase().includes(q) ||
      c.codigo.toLowerCase().includes(q) ||
      (c.cidade || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.telefone || '').includes(q) ||
      (c.celular || '').includes(q)
    );
  }, [estado.clientes, busca]);

  // ---- helpers de ação ----
  const addToast = (tipo: 'ok' | 'err', msg: string) => {
    const id = Date.now();
    setToasts(ts => [...ts, { id, tipo, msg }]);
    setTimeout(() => setToasts(ts => ts.filter(t => t.id !== id)), 4000);
  };

  const abrirWhatsapp = (c: ClienteGix) => {
    const numero = (c.celular || c.telefone || '').replace(/\D/g, '');
    if (!numero) { addToast('err', 'Cliente sem telefone cadastrado.'); return; }
    const num55 = numero.startsWith('55') ? numero : '55' + numero;
    window.dispatchEvent(new CustomEvent('crm-open-inbox', { detail: { numero: num55 } }));
  };

  const importarComoContato = async (c: ClienteGix) => {
    setImportando(c.codigo);
    try {
      await crmApi.criarContato(session, {
        nome: c.nome,
        email: c.email || undefined,
        telefone: c.celular || c.telefone || undefined,
        empresa: c.cnpj || undefined,
        tipo: 'cliente',
        responsavelId: meuEmail || undefined,
        endereco: { cidade: c.cidade, estado: c.estado },
        camposCustom: { gix_codigo: c.codigo, gix_cnpj: c.cnpj },
      });
      addToast('ok', `"${c.nome}" importado para Contatos!`);
    } catch (e: any) {
      addToast('err', e.message || 'Erro ao importar contato');
    } finally { setImportando(null); }
  };

  return (
    <div className="p-4 sm:p-6">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-50 space-y-2 pointer-events-none">
        {toasts.map(t => (
          <div key={t.id} className={`flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm text-white pointer-events-auto transition-all ${t.tipo === 'ok' ? 'bg-green-600' : 'bg-red-600'}`}>
            {t.tipo === 'ok' ? <Check className="h-4 w-4 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 flex-shrink-0" />}
            {t.msg}
          </div>
        ))}
      </div>

      {/* Modal Adicionar ao Funil */}
      {modalFunil && (
        <ModalAdicionarFunil
          cliente={modalFunil}
          session={session}
          onClose={() => setModalFunil(null)}
          onSucesso={msg => addToast('ok', msg)}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Briefcase className="h-6 w-6" /> Carteira GIX
          </h1>
          <p className="text-sm text-gray-600">
            Clientes vinculados ao seu cadastro de vendedor no GIX
            {estado.vendedorNome && (
              <span className="ml-1 text-gray-500">
                — <strong>{estado.vendedorNome}</strong> (cod. {estado.vendedorCodigo})
              </span>
            )}
          </p>
        </div>
        <button
          onClick={resolverEcarregar}
          disabled={estado.carregando}
          className="text-sm px-3 py-2 border border-gray-300 rounded-lg bg-white hover:bg-gray-50 flex items-center gap-1.5 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${estado.carregando ? 'animate-spin' : ''}`} /> Atualizar
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="flex-1 min-w-[220px] relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={busca}
            onChange={e => setBusca(e.target.value)}
            placeholder="Buscar por nome, codigo, cidade, email, telefone..."
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 px-3 py-2 border border-gray-200 rounded-lg bg-white">
          <input
            type="checkbox"
            checked={incluirInativos}
            onChange={e => setIncluirInativos(e.target.checked)}
          />
          Incluir inativos
        </label>
        <span className="text-xs text-gray-500">
          {filtrados.length} de {estado.total} cliente{estado.total !== 1 ? 's' : ''}
        </span>
      </div>

      {estado.carregando && (
        <div className="text-sm text-gray-500 py-12 text-center">Carregando carteira...</div>
      )}

      {!estado.carregando && estado.erro && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 items-start">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">{estado.erro}</div>
        </div>
      )}

      {!estado.carregando && !estado.erro && filtrados.length === 0 && (
        <div className="text-sm text-gray-500 py-12 text-center">
          Nenhum cliente encontrado.
        </div>
      )}

      {!estado.carregando && !estado.erro && filtrados.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-2">Codigo</th>
                <th className="text-left px-4 py-2">Cliente</th>
                <th className="text-left px-4 py-2">Cidade / UF</th>
                <th className="text-left px-4 py-2">Contato</th>
                <th className="text-right px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map(c => (
                <tr key={c.codigo} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">{c.codigo}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">{c.nome}</div>
                    {c.inativo === 'S' && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">INATIVO</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700">
                    {(c.cidade || c.estado) ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3 text-gray-400" />
                        {c.cidade}{c.cidade && c.estado ? ' / ' : ''}{c.estado}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-2">
                    <div className="space-y-0.5 text-xs">
                      {c.email && (
                        <div className="flex items-center gap-1 text-gray-700">
                          <Mail className="h-3 w-3 text-gray-400" /> {c.email}
                        </div>
                      )}
                      {c.telefone && (
                        <div className="flex items-center gap-1 text-gray-700">
                          <Phone className="h-3 w-3 text-gray-400" /> {c.telefone}
                        </div>
                      )}
                      {c.celular && (
                        <div className="flex items-center gap-1 text-gray-700">
                          <Smartphone className="h-3 w-3 text-gray-400" /> {c.celular}
                        </div>
                      )}
                      {!c.email && !c.telefone && !c.celular && (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center justify-end gap-1">
                      {/* WhatsApp */}
                      <button
                        onClick={() => abrirWhatsapp(c)}
                        title="Conversar no WhatsApp"
                        className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 transition-colors"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </button>
                      {/* Importar como Contato */}
                      <button
                        onClick={() => importarComoContato(c)}
                        disabled={importando === c.codigo}
                        title="Importar como Contato no CRM"
                        className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-40"
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                      {/* Adicionar ao Funil */}
                      <button
                        onClick={() => setModalFunil(c)}
                        title="Adicionar ao Funil de Vendas"
                        className="p-1.5 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors"
                      >
                        <TrendingUp className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CRMCarteiraGix;
